import { request } from "utils/api";
import { serialize } from "object-to-formdata";
import { Card, BankAccount } from "@stripe/stripe-js";

export type SubscriptionType = "standard" | "advanced" | "advanced_plus";
export type SubscriptionStatus = "active" | "inactive";
type StripeSubscriptionInfos = {
    cancel_at_period_end: boolean;
    cancel_at?: number;
    current_period_end: number;
    current_period_start: number;
    status: string;
};
type StripPrice = {
    id: string,
    object: string,
    active: boolean,
    billing_scheme: string,
    created: number,
    currency: string,
    livemode: boolean,
    lookup_key: string,
    metadata: any,
    nickname: string,
    product: any,
    recurring: any,
    tax_behavior: string,
    tiers_mode: string,
    transform_quantity: string,
    type: string,
    unit_amount: number,
    unit_amount_decimal: number
};
type StripCard = {
    brand: string,
    checks: any
    country: string,
    exp_month: number,
    exp_year: number,
    fingerprint: string,
    funding: string,
    generated_from: any,
    last4: number,
    networks: any,
    three_d_secure_usage: any
    wallet: any
};

export interface SubscriptionPlan {
    subscriptionType: SubscriptionType
    stripePriceId: string;
    priceCentsPerSeat: number;
    title: string;
    description: string;
}

export interface PaymentMethod {
    id: string;
    type: string;
    is_default_source: boolean;
}

export interface DefaultPaymentMethod {
    id: string;
    type: string;
    card: Card;
    sepa_debit: BankAccount;
}

export interface Subscription {
    id: string;
    subscriptionType: SubscriptionType
    subscriptionStatus: SubscriptionStatus;
    numberOfSeats: number;
    stripePriceId: string;
}

export interface SubscriptionSingle {
    id: string;
    subscriptionType: SubscriptionType;
    subscriptionStatus: SubscriptionStatus;
    numberOfSeats: number;
    stripePriceId: string;
    subscriptionInfos: StripeSubscriptionInfos
    latestInvoice: any,
    upcomingInvoice: any,
    price: StripPrice,
    card?: StripCard
    sepaDebit?: any,
}

export interface SubscriptionDetail extends SubscriptionSingle {
    pricePerSeat: number;
    totalPrice: number;
    renewalDate: number;
}

export interface CompanySubscriptionDetails extends Subscription {
    subscriptionInfos: StripeSubscriptionInfos;
    card?: StripCard;
    price?: StripPrice;
    latestInvoice?: any;
    upcomingInvoice?: any;
}

export type EditCompanySubscriptionPreview = {
    immediateTotalCents: number;
    nextInvoiceTotalCents: number;
    upcomingInvoice: any;
};

interface MetadataParam {
    [name: string]: string | number | null;
}
type Emptyable<T> = null | "" | T;
interface CustomField {
    name: string;
    value: string;
}
interface InvoiceSettingsParam {
    custom_fields: Emptyable<Array<CustomField>>;
    default_payment_method?: string;
    footer?: string;
}
interface TaxParam {
    ip_address?: Emptyable<string>;
}
type StripeCustomerAddress = {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postal_code: string;
    state: string;
};

export type StripeCustomer = {
    address?: StripeCustomerAddress;
    description?: string;
    email?: string;
    metaData?: MetadataParam;
    name?: string;
    phone?: string;
    shipping?: StripeCustomerAddress;
    balance?: number;
    coupon?: string;
    default_source?: string;
    invoice_prefix?: string;
    invoice_settings?: InvoiceSettingsParam;
    next_invoice_sequence?: number;
    preferred_locales?: string[];
    promotion_code?: string;
    source?: string;
    tax?: TaxParam;
    tax_exempt?: "exempt" | "none" | "reverse";
};

export type CompanySettings = {
    customer: StripeCustomer;
};

export type PublicMediaItem = {
    url: string;
    name: string;
    thumbnailUrl?: string;
};

export type CompanyProfile = {
    email: string;
    firstName: string;
    lastName: string;
    statusText: string;
    profilePictureUrl: string;
    galleryPictureUrls: string[];
    mediaItems: PublicMediaItem[];
};

export interface CompanySubscriptionParams {
    clubId: string,
    stripePriceId: string,
    numberOfSeats: number,
    paymentMethodId?: string | undefined,
    payManuallyViaInvoice?: boolean | undefined,
}

const subscriptionUrl = "/company/subscriptions";
const companySettingsUrl = "/company/settings";
const companyProfileUrl = "/company/profile";
const defaultPaymentMethodUrl = "/company/default-payment-method";

// fetch company subscripton (GET /company/subscriptions)
export const getCompanySubscriptons = (data: {
    clubId: string
}) => request<Subscription[]>({
    data,
    endpoint: subscriptionUrl,
    method: "get",
});

export const getAvailableSubscriptons = (data: {
    clubId: string
}) => request<SubscriptionPlan[]>({
    data,
    endpoint: `${subscriptionUrl}/available`,
    method: "get",
});

export const getSubscriptonsSingle = (data: {
    subscriptionId: string
}) => request<SubscriptionSingle>({
    data,
    endpoint: `${subscriptionUrl}/single`,
    method: "get",
});

// submit company subscripton (POST /company/subscriptions)
export const postCompanySubscriptons = (data: CompanySubscriptionParams) => request<CompanySubscriptionDetails>({
    data,
    endpoint: subscriptionUrl,
    method: "post",
});

// cancel subscription (DELETE /company/subscriptions)
export const deleteSubscription = (data: {
    subscriptionId: string
}) => request<CompanySubscriptionDetails>({
    data,
    endpoint: subscriptionUrl,
    method: "delete",
});

export const patchPreviewSubscription = (data: {
    subscriptionId: string,
    stripePriceId: string,
    numberOfSeats: number
}) => request<EditCompanySubscriptionPreview>({
    data,
    endpoint: `${subscriptionUrl}/preview`,
    method: "patch",
});

export const patchExtendSubscription = (data: {
    subscriptionId: string,
}) => request<CompanySubscriptionDetails>({
    data,
    endpoint: `${subscriptionUrl}/renew`,
    method: "patch",
});

export const patchCompanySubscription = (data: {
    subscriptionId: string,
    stripePriceId: string,
    numberOfSeats: number
}) => request<CompanySubscriptionDetails>({
    data,
    endpoint: subscriptionUrl,
    method: "patch",
});

export const getCompanySettings = (data: {
    clubId: string,
}) => request<CompanySettings>({
    data,
    endpoint: companySettingsUrl,
    method: "get",
});

export const updateCompanySettings = (data: {
    clubId: string,
    changes?: any
}) => request<CompanySettings>({
    data,
    endpoint: companySettingsUrl,
    method: "patch",
});

export const getCompanyProfile = (data: {
    clubId: string
}) => request<CompanyProfile>({
    data,
    endpoint: companyProfileUrl,
    method: "get",
});

export const postCompanyProfile = (data: {
    clubId: string
}) => request<CompanyProfile>({
    data,
    endpoint: companyProfileUrl,
    method: "post",
});

export const patchCompanyProfile = (data: {
    clubId: string,
    firstName?: string,
    lastName?: string,
    statusText?: string,
    profilePicture?: File;
    deleteProfilePicture?: boolean;

    galleryPicture?: File;
    deleteGalleryPictureIndex?: number;

    mediaItemName?: string;
    mediaItem?: File;
    mediaItemThumbnail?: File;

    removeMediaItemName?: string;
}) => request<CompanyProfile>({
    data: serialize(data),
    endpoint: companyProfileUrl,
    method: "patch",
});

export const updateCompanyProfilePassword = (data: {
    clubId: string,
    password: string,
}) => request<any>({
    data,
    endpoint: `${companyProfileUrl}/password`,
    method: "put",
});

export const getDefaultPaymentMethod = (data: {
    clubId: string
}) => request<DefaultPaymentMethod>({
    data,
    endpoint: defaultPaymentMethodUrl,
    method: "get",
});

export const putDefaultPaymentMethod = (data: {
    clubId: string,
    paymentMethodId: string,
}) => request<DefaultPaymentMethod>({
    data,
    endpoint: defaultPaymentMethodUrl,
    method: "put",
});
